import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { TrendChartPopup, TrendIconButton } from '../trend';
// Shared Field-Data column-config engine — the read-only tab columns run through
// the SAME assembler as Field Data Entry / Validation. This per-job Tracking
// detail uses only the STATIC base column sets (read-only), so it passes an empty
// field-config (fieldConfig=[] · *Dyn=EMPTY_DYN → the assembler no-ops) and then
// strips inline editors (stripEditable).
import {
  type FieldConfigItem, EMPTY_DYN,
  assembleTab, applyFieldConfig, stripEditable,
  STATION_FIELD_MAP, SAMPLING_FIELD_MAP, MONITORING_FIELD_MAP,
} from './fieldDataColumns';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useNavActions } from '../../hooks/useNavActions';
import { type IslandStep } from '../../components/StepIsland/StepIsland';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import { StepHint } from '../../components/StepHint/StepHint';
import { useConfirm } from '../../components/confirm';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { useNotify } from '../../components/notifications/useNotify';
import { runLoading } from '../../components/loading';
import { fmtLocalDate, fmtLocalDateTime } from '../../utils/datetime';
import { DateRangeSelect, TRACKING_RANGES } from '../modules/DateRangeSelect';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface TrackingListRow {
  jobId:             string;
  jobOid:            string;
  client:            string;
  projectId:         string;
  projectName:       string;
  sampleMatrix:      string;
  status:            string;
  statusId:          string;
  samplingStatusInt: number;
  submitted:         string | null;
  validated:         string | null;
  approved:          string | null;
  lastAction:        string | null;
  receivedDate:      string | null;
}

/**
 * Unique key for a Field Data Tracking list row.
 *
 * AI_FieldDataTracking_List_SP groups by (JobID, SamplingStatus), so a job that
 * sits in several workflow stages returns SEVERAL rows that all share the same
 * jobOid. Keying the grid on jobOid alone made the selection checkbox tick
 * every stage of that job at once (user report 2026-09-24 — ALPACA's
 * SampleLogIn_ListView_FieldDataTracking ticks exactly one row, because each of
 * its rows is a distinct SampleLogIn object). The status discriminator restores
 * one-row-per-tick parity.
 */
const rowKey = (r: TrackingListRow) => `${r.jobOid}:${r.samplingStatusInt}`;

interface TrackingSummary {
  jobId:            string;
  jobOid:           string;
  client:           string;
  projectId:        string;
  projectName:      string;
  sampleMatrix:     string;
  samplingStatusInt: number;
  submittedBy:      string | null;
  submittedDate:    string | null;
  validatedBy:      string | null;
  validatedDate:    string | null;
  approvedBy:       string | null;
  approvedDate:     string | null;
  rolledBackBy:     string | null;
  rolledBackDate:   string | null;
  rollbackReason:   string | null;
}

interface StationRow {
  sampleLoginOid: string;
  stationId:      string;
  stationName:    string;
  blended:        boolean;
  latitude:       string | null;
  longitude:      string | null;
  plannedLatitude:  string | null;
  plannedLongitude: string | null;
  temperature:    string | null;
  humidity:       string | null;
  stationComment: string | null;
  [key: string]:  unknown; // dynamic (config-driven) extra fields
}

interface SamplingRow {
  sampleLoginOid: string;
  stationId:      string;
  stationName:    string;
  sampleType:     string;
  sampleId:       string;
  collectorOid:   string | null;
  collectorName:  string | null;
  collectedDate:  string | null;
  sampleComment:  string;
  testSummary:    string;
  sampleStatus:   string | null;
  enteredBy:      string;
  enteredDate:    string | null;
  [key: string]:  unknown;
}

interface MonitoringRow {
  sampleParameterOid: string;
  sampleLoginOid:     string;
  sampleId:           string;
  test:               string;
  methodName:         string;
  parameter:          string;
  result:             string;
  unit:               string;
  monitoredBy:        string;
  monitoredDate:      string | null;
  enteredBy:          string;
  enteredDate:        string | null;
  [key: string]:      unknown;
}

type TabKey = 'stations' | 'sampling' | 'monitoring';
type DateRangeKey = '1M' | '3M' | '6M' | '1Y' | 'All';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function statusPillVariant(statusId: string): string {
  if (statusId === 'FD-2-1' || statusId === 'FD-2-2') return 'warn';
  if (statusId === 'FD-2-6') return 'ok';
  return 'info';
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function FieldDataTrackingPage() {
  // ── List state (front view — every job in the field-data pipeline) ──────────
  const [listRows,    setListRows]    = useState<TrackingListRow[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listLoadMs,  setListLoadMs]  = useState<number | null>(null);
  const [listError,   setListError]   = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [listVisible, setListVisible] = useState<TrackingListRow[]>([]);
  const [search,      setSearch]      = useState('');
  const [dateRange,   setDateRange]   = useState<DateRangeKey>('3M');

  // ── Detail state (one job opened from the list) ─────────────────────────────
  const [view,          setView]          = useState<'list' | 'detail'>('list');
  const [selectedJob,   setSelectedJob]   = useState<TrackingListRow | null>(null);
  const [summary,       setSummary]       = useState<TrackingSummary | null>(null);
  const [activeTab,     setActiveTab]     = useState<TabKey>('stations');
  const [stations,      setStations]      = useState<StationRow[]>([]);
  const [sampling,      setSampling]      = useState<SamplingRow[]>([]);
  const [monitoring,    setMonitoring]    = useState<MonitoringRow[]>([]);
  const [stationVisible,    setStationVisible]    = useState<StationRow[]>([]);
  const [samplingVisible,   setSamplingVisible]   = useState<SamplingRow[]>([]);
  const [monitoringVisible, setMonitoringVisible] = useState<MonitoringRow[]>([]);
  const [selSamplingIds,   setSelSamplingIds]   = useState<Set<string | number>>(new Set());
  const [selStationIds,    setSelStationIds]    = useState<Set<string | number>>(new Set());
  const [selMonitoringIds, setSelMonitoringIds] = useState<Set<string | number>>(new Set());
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailLoadMs,  setDetailLoadMs]  = useState<number | null>(null);
  const detailT0 = useRef<number>(0);

  // Permission gate ported from GoLive FieldDataReviewController.OnActivated —
  // Rollback is enabled only for admins or roles with Write=true on the
  // FieldDataTracking navigation permission.
  const [canWrite, setCanWrite] = useState(true);
  const perm = useNavActions('fielddata');
  const canWriteEff = canWrite && perm.write;

  // ── Trend popup (Monitoring "Trend" column + toolbar Trending button) ───────
  const [trendOpen,  setTrendOpen]  = useState(false);
  const [trendSite,  setTrendSite]  = useState('');
  const [trendParam, setTrendParam] = useState('');

  const notify                = useNotify();
  const { prompt: promptDlg } = useConfirm();

  // ── Load list (jobs in the field-data pipeline, by submitted-date window) ────
  const loadList = useCallback(async (range: DateRangeKey, searchText?: string) => {
    setListLoading(true);
    setListError(null);
    const t0 = performance.now();
    try {
      const params: Record<string, string> = {};
      if (range !== 'All') params.range = range;
      if (searchText) params.search = searchText;
      const res = await api.get('/api/v1/field-data-tracking', { params });
      setListRows((res.data as { data: TrackingListRow[] }).data ?? []);
      setSelectedIds(new Set());
    } catch (e: unknown) {
      setListError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setListLoadMs(performance.now() - t0);
      setListLoading(false);
    }
  }, []);

  useEffect(() => { void loadList(dateRange); }, [loadList, dateRange]);

  // Single-select (radio-style) — only one job may be ticked at a time (Load
  // opens one record). Header "select all" collapses to the last row.
  const handleSelectionChange = useCallback((next: Set<string | number>) => {
    if (next.size <= 1) { setSelectedIds(next); return; }
    const newlyAdded = [...next].find(id => !selectedIds.has(id));
    setSelectedIds(new Set([newlyAdded ?? [...next][next.size - 1]!]));
  }, [selectedIds]);

  // ── Permission gate ─────────────────────────────────────────────────────────
  useEffect(() => {
    api.get('/api/v1/field-data-tracking/permissions')
      .then(res => setCanWrite(Boolean((res.data as { data: { canWrite: boolean } }).data?.canWrite)))
      .catch(() => setCanWrite(false));
  }, []);

  // ── Open a job's full record (Workflow Summary + read-only tabs) ────────────
  const openDetail = useCallback(async (job: TrackingListRow) => {
    setSelectedJob(job);
    setView('detail');
    setDetailLoading(true);
    setActiveTab('stations');
    setSummary(null);
    setStations([]); setSampling([]); setMonitoring([]);
    setSelStationIds(new Set()); setSelSamplingIds(new Set()); setSelMonitoringIds(new Set());
    detailT0.current = performance.now();
    try {
      // GoLive parity (FieldDataReviewController): Field Data Tracking runs in
      // strMode == "View", where CustomProcessSelectedItem captures BOTH the job
      // (FDERInfo.TaskRegistrationOid) AND the clicked row's SamplingStatus
      // (FDERInfo.strStatus), and every detail tab then filters
      //   [Samplelogin.JobID.Oid] = @job AND [Samplelogin.SamplingStatus] = @status
      // (ViStation_ControlCreated line ~365 · Sampling line ~272 · Monitoring
      // line ~287). Omitting the status showed the WHOLE job — every stage at
      // once — where GoLive shows only the ticked stage's samples (user report
      // 2026-09-24: job 260901057 FD-2-3 → GoLive 3 stations, AI 57).
      const statusParams = { params: { status: job.samplingStatusInt } };
      const [sm, st, sp, mn] = await Promise.all([
        api.get(`/api/v1/field-data-tracking/${job.jobOid}/summary`),
        api.get(`/api/v1/field-data-tracking/${job.jobOid}/stations`,   statusParams),
        api.get(`/api/v1/field-data-tracking/${job.jobOid}/sampling`,   statusParams),
        api.get(`/api/v1/field-data-tracking/${job.jobOid}/monitoring`, statusParams),
      ]);
      setSummary((sm.data as { data: TrackingSummary | null }).data ?? null);
      setStations((st.data as { data: StationRow[] }).data ?? []);
      setSampling((sp.data as { data: SamplingRow[] }).data ?? []);
      setMonitoring((mn.data as { data: MonitoringRow[] }).data ?? []);
      setDetailLoadMs(performance.now() - detailT0.current);
    } catch {
      notify.error('Failed to load job record. Please try again.');
    } finally {
      setDetailLoading(false);
    }
  }, [notify]);

  const closeDetail = useCallback(() => {
    setView('list');
    setSelectedJob(null);
    setSummary(null);
    setStations([]); setSampling([]); setMonitoring([]);
    setSelStationIds(new Set()); setSelSamplingIds(new Set()); setSelMonitoringIds(new Set());
    void loadList(dateRange, search);
  }, [loadList, dateRange, search]);

  // ── Rollback (detail) — per SELECTED sample on the Sampling Information tab,
  // matching GoLive FieldDataReviewController.RollBack_Accepting (per-sample). ──
  const handleRollback = useCallback(async () => {
    if (!selectedJob) return;
    if (!canWriteEff) { notify.error('You do not have permission to roll back field data.'); return; }
    const picked = sampling
      .filter(r => selSamplingIds.has(r.sampleLoginOid))
      .map(r => r.sampleLoginOid);
    if (picked.length === 0) {
      notify.error('Select at least one sample to roll back.');
      setActiveTab('sampling');
      return;
    }
    const reason = await promptDlg({
      title: 'Rollback Reason',
      message: `Enter reason for rolling back ${picked.length} selected sample${picked.length > 1 ? 's' : ''} in job ${selectedJob.jobId}:`,
      required: true,
    });
    if (!reason) return;
    try {
      await runLoading(
        api.post(`/api/v1/field-data-tracking/${selectedJob.jobOid}/rollback`, {
          reason, selectedSampleOids: picked,
        }),
        { blocking: true, label: 'Rolling back…' },
      );
      notify.success('Rolled back to Pending Submission (data intact).');
      closeDetail();
    } catch (err: unknown) {
      const raw = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
      const msg = typeof raw === 'string' ? raw : (raw as { message?: string } | undefined)?.message ?? 'Rollback failed.';
      notify.error(msg);
    }
  }, [selectedJob, canWriteEff, sampling, selSamplingIds, promptDlg, notify, closeDetail]);

  // ── Trend ────────────────────────────────────────────────────────────────────
  const stationNameByLogin = useMemo(
    () => new Map(stations.map((s) => [s.sampleLoginOid, s.stationName])),
    [stations],
  );
  const openTrend = useCallback((siteName: string, parameter: string) => {
    setTrendSite(siteName);
    setTrendParam(parameter);
    setTrendOpen(true);
  }, []);

  // ── Island stepper ──────────────────────────────────────────────────────────
  const islandSteps = useMemo((): IslandStep[] => {
    if (view === 'list') {
      return [
        { id: 'job',        label: 'Select Job', state: selectedIds.size > 0 ? 'done' : 'active' },
        { id: 'entry',      label: 'Entry',      state: 'todo' },
        { id: 'validation', label: 'Validation', state: 'todo' },
        { id: 'approval',   label: 'Approval',   state: 'todo' },
      ];
    }
    return [
      { id: 'job',        label: 'Select Job', state: 'done' },
      { id: 'entry',      label: 'Entry',      state: summary?.submittedDate ? 'done' : 'active' },
      { id: 'validation', label: 'Validation', state: summary?.validatedDate ? 'done' : summary?.submittedDate ? 'active' : 'todo' },
      { id: 'approval',   label: 'Approval',   state: summary?.approvedDate ? 'done' : summary?.validatedDate ? 'active' : 'todo' },
    ];
  }, [view, selectedIds, summary]);

  // ── Free-text filter over the list ──────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!search.trim()) return listRows;
    const t = search.toLowerCase();
    // Null-safe: projectId/projectName (and others) can be null from the API for a
    // job with no project. A bare `.toLowerCase()` threw inside this useMemo when a
    // longer search stopped matching jobId and fell through to a null field —
    // crashing the whole page to blank (user report 2026-09-25, full JobID search).
    const has = (v: string | null | undefined) => (v ?? '').toLowerCase().includes(t);
    return listRows.filter(x =>
      has(x.jobId) || has(x.client) || has(x.projectId) ||
      has(x.projectName) || has(x.status) || has(x.statusId));
  }, [listRows, search]);

  // ── List grid columns — ALPACA Field Data Tracking parity ───────────────────
  const listCols: AppColumn<TrackingListRow>[] = [
    { field: 'jobId',       header: 'Job ID',      width: 120 },
    { field: 'client',      header: 'Client',      width: 200 },
    { field: 'projectId',   header: 'Project ID',  width: 130 },
    { field: 'status',      header: 'Status',      width: 210,
      render: r => <span className={`bl-pill ${statusPillVariant(r.statusId)}`}>{r.status}</span> },
    { field: 'statusId',    header: 'Status ID',   width: 90 },
    { field: 'submitted',   header: 'Submitted',   width: 150, type: 'date',
      render: r => <span>{r.submitted ? fmtLocalDate(r.submitted) : ''}</span> },
    { field: 'validated',   header: 'Validated',   width: 150, type: 'date',
      render: r => <span>{r.validated ? fmtLocalDate(r.validated) : ''}</span> },
    { field: 'approved',    header: 'Approved',    width: 150, type: 'date',
      render: r => <span>{r.approved ? fmtLocalDate(r.approved) : ''}</span> },
    { field: 'lastAction',  header: 'Last Action', width: 160, type: 'datetime',
      render: r => <span>{r.lastAction ? fmtLocalDateTime(r.lastAction) : ''}</span> },
  ];

  // ── Base (fixed) read-only grid columns for the three detail tabs ───────────
  const stationBase: AppColumn<StationRow>[] = [
    { field: 'stationId',      header: 'JobID',             width: 120 },
    { field: 'stationName',    header: 'StationLocation',   width: 160 },
    { field: 'blended',        header: 'Blended',           width: 90, align: 'center',
      render: r => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <input type="checkbox" checked={r.blended} readOnly
            style={{ cursor: 'default', width: 14, height: 14 }} />
        </div>
      ) },
    { field: 'latitude',       header: 'Latitude',          width: 120 },
    { field: 'longitude',      header: 'Longitude',         width: 120 },
    { field: 'plannedLatitude',  header: 'PlannedLatitude',  width: 130 },
    { field: 'plannedLongitude', header: 'PlannedLongitude', width: 130 },
    { field: 'temperature',    header: 'Temperature',       width: 140 },
    { field: 'humidity',       header: 'Humidity (%)',      width: 120 },
    { field: 'stationComment', header: 'StationComment',    width: 200 },
  ];
  const samplingBase: AppColumn<SamplingRow>[] = [
    { field: 'stationName',     header: 'StationLocation',  width: 150 },
    { field: 'sampleId',        header: 'SampleID',         width: 140 },
    { field: 'collectorName',   header: 'CollectedBy',      width: 140 },
    { field: 'collectedDate',   header: 'CollectedDate',    width: 160, type: 'datetime',
      render: r => <span>{r.collectedDate ? fmtLocalDateTime(r.collectedDate) : ''}</span> },
    { field: 'testSummary',     header: 'TestSummary',      width: 340 },
    { field: 'sampleComment',   header: 'SampleComment',    width: 180 },
    { field: 'sampleStatus',    header: 'SampleStatus',     width: 150 },
    { field: 'enteredBy',       header: 'EnteredBy',        width: 120 },
    { field: 'enteredDate',     header: 'EnteredDate',      width: 160, type: 'date',
      render: r => <span>{r.enteredDate ? fmtLocalDate(r.enteredDate) : ''}</span> },
  ];
  const monitoringBase: AppColumn<MonitoringRow>[] = [
    { field: 'sampleId',    header: 'SampleID',       width: 140 },
    { field: 'test',        header: 'Test',           width: 180 },
    { field: 'methodName',  header: 'MethodName',     width: 140 },
    { field: 'parameter',   header: 'Parameter',      width: 180 },
    { field: 'result',      header: 'Result',         width: 90, align: 'right' },
    { field: 'unit',        header: 'Units',          width: 110 },
    { field: 'monitoredBy', header: 'MonitoredBy',    width: 130 },
    { field: 'monitoredDate', header: 'MonitoredDate', width: 160, type: 'datetime',
      render: r => <span>{r.monitoredDate ? fmtLocalDateTime(r.monitoredDate) : ''}</span> },
    { field: 'enteredBy',   header: 'EnteredBy',      width: 120 },
    { field: 'enteredDate', header: 'EnteredDate',    width: 160, type: 'date',
      render: r => <span>{r.enteredDate ? fmtLocalDate(r.enteredDate) : ''}</span> },
  ];
  const trendCol: AppColumn<MonitoringRow> = {
    field: 'trend', header: 'Trend', headerText: 'Trend', width: 64, align: 'center',
    sortable: false, filterable: false, noExport: true,
    render: r => {
      const st = stationNameByLogin.get(r.sampleLoginOid) ?? '';
      return <TrendIconButton onClick={() => openTrend(st, r.parameter)}
        disabledReason={st ? undefined : 'No station for this row'} />;
    },
  };

  const EMPTY_FIELD_CONFIG: FieldConfigItem[] = [];
  const stationCols = useMemo(
    () => stripEditable(assembleTab(applyFieldConfig(stationBase, EMPTY_FIELD_CONFIG, 1, STATION_FIELD_MAP), EMPTY_DYN, EMPTY_FIELD_CONFIG, 1, STATION_FIELD_MAP).cols),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
  const samplingCols = useMemo(
    () => stripEditable(assembleTab(applyFieldConfig(samplingBase, EMPTY_FIELD_CONFIG, 0, SAMPLING_FIELD_MAP), EMPTY_DYN, EMPTY_FIELD_CONFIG, 0, SAMPLING_FIELD_MAP).cols),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
  const monitoringCols = useMemo(
    () => [...stripEditable(assembleTab(applyFieldConfig(monitoringBase, EMPTY_FIELD_CONFIG, 2, MONITORING_FIELD_MAP), EMPTY_DYN, EMPTY_FIELD_CONFIG, 2, MONITORING_FIELD_MAP).cols), trendCol],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stationNameByLogin]);

  const loadedJob = selectedIds.size === 1
    ? listRows.find(r => selectedIds.has(rowKey(r))) ?? null
    : null;

  // Field Data Tracking keys (standing rule 14, §18.6, ADR-0013):
  //  · LIST view   — Alt+L Load (opens the ticked job).
  //  · DETAIL view — Alt+B Rollback (per ticked Sampling sample).
  useHotkeys(view === 'list'
    ? [{ combo: 'alt+l', label: 'Load', group: 'Field Data Tracking', order: 1,
        run: () => { if (loadedJob) void openDetail(loadedJob); }, disabled: !loadedJob }]
    : [
        { combo: 'alt+b', label: 'Rollback', group: 'Field Data Tracking', order: 1,
          run: () => { void handleRollback(); }, disabled: selSamplingIds.size === 0 || !canWriteEff },
        { combo: 'esc', label: 'Back to list', group: 'Field Data Tracking', order: 2,
          allowInInput: false, run: () => { closeDetail(); } },
      ]);

  // ═══════════════════════════════════════════════════════════════════
  // LIST VIEW — every job in the field-data pipeline (front view)
  // ═══════════════════════════════════════════════════════════════════
  if (view === 'list') {
    return (
      <div className="bl-page">
        <TopbarStepper steps={islandSteps} />

        <div className="bl-top">
          <div className="dp-page-hdr">
            <div className="dp-breadcrumb">
              <span>Home</span><i className="ti ti-chevron-right" />
              <span>Sampling Management</span><i className="ti ti-chevron-right" />
              <span className="dp-bc-active">Field Data Tracking</span>
            </div>

            <div className="dp-title-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormTitle pageId="fielddata" auditFormName="Field Data Tracking"
                loadMs={listLoadMs} loading={listLoading} />
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  className="ca-btn ca-btn-solid"
                  disabled={!loadedJob}
                  onClick={() => { if (loadedJob) void openDetail(loadedJob); }}
                  title={!loadedJob ? 'Tick a Job ID to load' : `Load ${loadedJob.jobId} (${formatCombo('alt+l')})`}
                >
                  <span aria-hidden="true">⬇️</span>&nbsp;Load
                </button>
              </div>
            </div>

            <StepHint tone="guidance">
              <b>Tracking.</b> Every job in the field-data pipeline with who did what, when — jobs appear here from their first <b>Submit</b> (FD-2-3 → FD-2-4 → FD-2-6). Tick a <b>Job ID</b> and click <b>Load</b> (or double-click a row) to open the full record, then <b>Rollback</b> (mandatory reason, audited) to return the selected samples to Pending Submission.
            </StepHint>
          </div>
        </div>

        <div className="bl-grid-area">
          <div style={{ margin: '0 0 8px' }}>
            <AppToolbar
              left={
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <DateRangeSelect
                    variant="dropdown"
                    label="Date Range"
                    ranges={TRACKING_RANGES}
                    value={TRACKING_RANGES.find(r => r.label === dateRange)?.days ?? null}
                    onChange={(d) => setDateRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)}
                  />
                  <div className="list-search" style={{ display: 'inline-flex', alignItems: 'center', minWidth: 240, height: 32 }}>
                    <span className="tb-i">🔍</span>
                    <input
                      type="text"
                      placeholder="Search..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                    {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
                  </div>
                </div>
              }
              onRefresh={() => void loadList(dateRange, search)}
              refreshing={listLoading}
              exportData={{ filename: 'field-data-tracking', ...gridExportMatrix(listVisible, listCols) }}
            />
          </div>

          {search && filtered.length !== listRows.length && (
            <div style={{ marginBottom: 6 }}>
              <span className="search-match-count">{filtered.length} match{filtered.length !== 1 ? 'es' : ''}</span>
            </div>
          )}

          <div className="bl-card bl-card-flex">
            {listError ? (
              <div className="bl-err" style={{ margin: 12 }}>
                <i className="ti ti-alert-circle" /> {listError}
                <button onClick={() => void loadList(dateRange, search)}
                  style={{ marginLeft: 10, color: 'var(--brand-accent,#e07b39)', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                  Retry
                </button>
              </div>
            ) : (
              <AppDataGrid<TrackingListRow>
                maximizable
                rows={filtered}
                columns={listCols}
                getRowId={rowKey}
                loading={listLoading}
                selectable
                selectedIds={selectedIds}
                onSelectionChange={handleSelectionChange}
                onVisibleRowsChange={setListVisible}
                /* Double-click (or Enter on the active row) opens that job's record —
                   the same flow as Tick a Job ID → Load (user request 2026-09-28). */
                onRowDoubleClick={r => void openDetail(r)}
                emptyText="No jobs in the field-data pipeline for this window."
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // DETAIL VIEW — Workflow Summary + read-only tabs
  // ═══════════════════════════════════════════════════════════════════
  const summaryRows: [string, string][] = [
    ['Submitted',       summary?.submittedDate ? `${fmtLocalDateTime(summary.submittedDate)}${summary.submittedBy ? ` · ${summary.submittedBy}` : ''}` : '—'],
    ['Validated',       summary?.validatedDate ? `${fmtLocalDateTime(summary.validatedDate)}${summary.validatedBy ? ` · ${summary.validatedBy}` : ''}` : '—'],
    ['Approved',        summary?.approvedDate ? `${fmtLocalDateTime(summary.approvedDate)}${summary.approvedBy ? ` · ${summary.approvedBy}` : ''}` : '—'],
    ['Rolled Back',     summary?.rolledBackDate ? `${fmtLocalDateTime(summary.rolledBackDate)}${summary.rolledBackBy ? ` · ${summary.rolledBackBy}` : ''}` : '—'],
    ['Rollback Reason', summary?.rollbackReason ?? '—'],
    ['StatusID',        selectedJob?.statusId ?? '—'],
  ];

  return (
    <div className="bl-page">
      <TopbarStepper steps={islandSteps} />

      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Sampling Management</span>
            <i className="ti ti-chevron-right" />
            <span
              style={{ color: 'var(--brand-accent,#e07b39)', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={closeDetail}
            >Field Data Tracking</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{selectedJob?.jobId}</span>
          </div>

          <div className="dp-title-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FormTitle
              pageId="fielddata"
              title={`Field Data Tracking — ${selectedJob?.jobId ?? ''}`}
              auditFormName="Field Data Tracking"
              loadMs={detailLoadMs}
              loading={detailLoading}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              {/* Back — return to the Field Data Tracking list (user request
                  2026-09-28); mirrors the breadcrumb, bound to Esc (rule 14). */}
              <button className="ca-btn" onClick={closeDetail}
                title={`Back to list (${formatCombo('esc')})`}>
                <span aria-hidden="true">←</span>&nbsp;Back
              </button>
              <button
                className="ca-btn"
                disabled={!canWriteEff || sampling.length === 0}
                title={!canWriteEff
                  ? 'You do not have permission to roll back field data'
                  : sampling.length === 0
                    ? 'No samples to rollback'
                    : selSamplingIds.size === 0
                      ? 'Tick the samples to roll back on the Sampling Information tab'
                      : `Roll back ${selSamplingIds.size} selected sample(s) (${formatCombo('alt+b')})`}
                onClick={() => void handleRollback()}
              >
                <span aria-hidden="true">↩️</span>&nbsp;Rollback
              </button>
            </div>
          </div>

          <StepHint tone="guidance">
            <b>Tracking · read-only.</b> The whole-job record — Entry → Validation → Approval — with who did what and when. Tick samples on <b>Sampling Information</b> and click <b>Rollback</b> to return them to Pending Submission (data intact).
          </StepHint>
        </div>
      </div>

      <div className="bl-grid-area">
        {/* Workflow Summary card — Entry · Validation · Approval */}
        <div className="bl-card" style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderBottom: '1px solid var(--line, #e8eaf0)', background: 'linear-gradient(180deg,#fff,#f7f8fb)' }}>
            <span style={{ width: 4, height: 16, borderRadius: 3, background: 'var(--brand-accent, #e07b39)', display: 'inline-block', flexShrink: 0 }} />
            <span style={{ fontWeight: 700, fontSize: 12.5, color: '#15203a' }}>Workflow Summary — Entry · Validation · Approval</span>
            {selectedJob && <span className={`bl-pill ${statusPillVariant(selectedJob.statusId)}`} style={{ marginLeft: 4 }}>{selectedJob.status}</span>}
          </div>
          <div style={{ padding: '7px 12px 8px', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '3px 18px' }}>
            {([
              ['JobID',        selectedJob?.jobId ?? ''],
              ['Client',       selectedJob?.client ?? ''],
              ['SampleMatrix', selectedJob?.sampleMatrix ?? ''],
              ['ProjectID',    selectedJob?.projectId ?? ''],
              ['ProjectName',  selectedJob?.projectName ?? ''],
              ...summaryRows,
            ] as [string, string][]).map(([lbl, val], i) => (
              <div key={`${lbl}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ width: 108, flexShrink: 0, fontSize: 11, color: '#4a5260' }}>{lbl}</label>
                <div style={{ flex: 1, minWidth: 0, padding: '2px 7px', border: '1px solid #e3e6ec', borderRadius: 4, fontSize: 11.5, background: '#f7f8fa', color: '#3a414e', height: 23, display: 'flex', alignItems: 'center', overflow: 'hidden', textOverflow: 'ellipsis' }} title={val}>{val || '—'}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Read-only notice + toolbar — sits ABOVE the tabs (user request
            2026-09-28: move the tab strip below the Refresh / Export bar). */}
        <div style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <AppToolbar
            left={activeTab === 'monitoring' ? (
              <button className="ca-btn" onClick={() => openTrend('', '')}
                title="Concentration trend — pick a site and parameter">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#e8670a" strokeWidth="2.4"
                  strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4, verticalAlign: '-2px' }}>
                  <path d="M3 17l5-5 4 3 7-8" /><path d="M15 4h5v5" /></svg>
                Trending
              </button>
            ) : undefined}
            onRefresh={() => { if (selectedJob) void openDetail(selectedJob); }}
            refreshing={detailLoading}
            exportData={{
              filename: `field-data-tracking-${activeTab}`,
              ...(activeTab === 'stations'  ? gridExportMatrix(stationVisible,    stationCols)
                : activeTab === 'sampling'  ? gridExportMatrix(samplingVisible,   samplingCols)
                :                             gridExportMatrix(monitoringVisible, monitoringCols)),
            }}
          />
          <span style={{ fontSize: 11, color: 'var(--ai-muted)', marginLeft: 4 }}>Read-only review.</span>
        </div>

        {/* ── Tabs (moved BELOW the toolbar, user request 2026-09-28) ── */}
        <div className="sa-filter-row" style={{ marginBottom: 8 }}>
          {(['stations', 'sampling', 'monitoring'] as TabKey[]).map(tab => {
            const labels: Record<TabKey, string> = {
              stations:   'Station Information',
              sampling:   'Sampling Information',
              monitoring: 'Monitoring Records',
            };
            return (
              <button
                key={tab}
                className={`sa-filter-btn${activeTab === tab ? ' active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {labels[tab]}
              </button>
            );
          })}
        </div>

        {selectedJob && (
          <TrendChartPopup
            open={trendOpen}
            onClose={() => setTrendOpen(false)}
            siteTrend={{
              siteName: trendSite,
              parameter: trendParam,
              siteOptions: trendSite ? [] : Array.from(new Set(stations.map((s) => s.stationName).filter(Boolean))),
              job: { jobId: selectedJob.jobId, client: selectedJob.client,
                     projectId: selectedJob.projectId, projectName: selectedJob.projectName },
            }}
          />
        )}

        {activeTab === 'stations' && (
          <div className="bl-card bl-card-flex">
            <AppDataGrid<StationRow>
              maximizable
              rows={stations}
              columns={stationCols}
              getRowId={r => r.sampleLoginOid}
              loading={detailLoading}
              emptyText="No station rows."
              selectable
              selectedIds={selStationIds}
              onSelectionChange={setSelStationIds}
              onVisibleRowsChange={setStationVisible}
            />
          </div>
        )}

        {activeTab === 'sampling' && (
          <div className="bl-card bl-card-flex">
            <AppDataGrid<SamplingRow>
              maximizable
              rows={sampling}
              columns={samplingCols}
              getRowId={r => r.sampleLoginOid}
              loading={detailLoading}
              emptyText="No sampling rows."
              selectable
              selectedIds={selSamplingIds}
              onSelectionChange={setSelSamplingIds}
              onVisibleRowsChange={setSamplingVisible}
            />
          </div>
        )}

        {activeTab === 'monitoring' && (
          <div className="bl-card bl-card-flex">
            <AppDataGrid<MonitoringRow>
              maximizable
              rows={monitoring}
              columns={monitoringCols}
              getRowId={r => r.sampleParameterOid}
              loading={detailLoading}
              emptyText="No monitoring rows."
              selectable
              selectedIds={selMonitoringIds}
              onSelectionChange={setSelMonitoringIds}
              onVisibleRowsChange={setMonitoringVisible}
            />
          </div>
        )}
      </div>
    </div>
  );
}
